<template>
  <v-row class="patient-information-container">
    <v-col v-if="orderInfoPreview" cols="12">
      <v-row class="patient-information">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Requesting Provider or Prescriber</h3>
        </v-col>

        <v-col cols="12" class="text-body-3-1 weight-600 text-primary">PROVIDER INFORMATION</v-col>

        <v-col v-if="props.isEditable && hasProviders" cols="12">
          <v-autocomplete
            v-model="selectedProvider"
            :items="providerSearchResults"
            :search="providerSearchTerm"
            :loading="providerSearchLoading"
            :custom-filter="providerCustomFilter"
            class="lh--force-white-background"
            label="Search Provider (Name or NPI)"
            placeholder="Start typing provider name or NPI"
            clearable
            return-object
            @update:search="handleProviderSearch"
            @update:model-value="handleProviderSelection">
            <template #item="{ props: itemProps, item }">
              <v-list-item v-if="item.raw?.isLoadMore" v-bind="itemProps" @click="loadMoreProviders">
                <template #title>
                  <span class="text-primary font-weight-medium">Load more results...</span>
                </template>
              </v-list-item>
              <v-list-item v-else v-bind="itemProps" lines="two">
                <template #title>
                  {{ item.value?.displayName || `${item.value?.firstName || ""} ${item.value?.lastName || ""}`.trim() }}
                </template>
                <template #subtitle>
                  <span class="text-caption">
                    NPI: {{ item.value?.npi || "" }} | {{ item.value?.address1 || "" }}, {{ item.value?.city || "" }},
                    {{ item.value?.state || "" }}
                  </span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="6">
          <FeedbackInputWrapper
            :field-id="'providerFirstName'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerFirstName"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerFirstName') && props.formButtonClicked
                ? 'Please enter provider first name.'
                : null
            "
            label="providerFirstName">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerFirstName"
                type="text"
                placeholder="First Name"
                label="First Name*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerFirstName', orderInfoPreview.providerFirstName);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="6">
          <FeedbackInputWrapper
            :field-id="'providerLastName'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerLastName"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerLastName') && props.formButtonClicked
                ? 'Please enter provider last name.'
                : null
            "
            label="providerLastName">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerLastName"
                type="text"
                placeholder="Last Name"
                label="Last Name*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerLastName', orderInfoPreview.providerLastName);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerContactPhone'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerContactPhone"
            :error-messages="
              props.isEditable && !phoneValid('providerContactPhone') && props.formButtonClicked
                ? 'Please enter provider contact phone number.'
                : null
            "
            label="providerContactPhone">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerContactPhone"
                type="text"
                placeholder="Contact Phone"
                label="Contact Phone*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerContactPhone', orderInfoPreview.providerContactPhone);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerContactFax'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerContactFax"
            :error-messages="
              props.isEditable && !phoneValid('providerContactFax') && props.formButtonClicked
                ? 'Please enter provider contact fax number.'
                : null
            "
            label="providerContactFax">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerContactFax"
                type="text"
                placeholder="Contact Fax"
                label="Contact Fax*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerContactFax', orderInfoPreview.providerContactFax);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerNpiNumber'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerNpiNumber"
            :error-messages="
              props.isEditable && !npiValid('providerNpiNumber') && props.formButtonClicked
                ? 'Please enter a valid provider NPI number.'
                : null
            "
            label="providerNpiNumber">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerNpiNumber"
                type="text"
                placeholder="NPI Number"
                label="NPI Number*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerNpiNumber', orderInfoPreview.providerNpiNumber);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12" class="mb-4">
          <v-divider />
        </v-col>

        <v-col cols="12" class="text-body-3-1 weight-600 text-primary">REQUESTING PROVIDER OR PRESCRIBER ADDRESS</v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'providerAddress1'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerAddress1"
            :error-messages="
              props.isEditable && !addressValid('providerAddress1') && props.formButtonClicked
                ? 'Please enter provider address. If it is over 35 characters, please use address line 2.'
                : null
            "
            label="providerAddress1">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerAddress1"
                type="text"
                placeholder="Address Line 1"
                label="Address Line 1*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerAddress1', orderInfoPreview.providerAddress1);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'providerAddress2'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerAddress2"
            label="providerAddress2">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.providerAddress2"
                type="text"
                placeholder="Address Line 2"
                label="Address Line 2"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerAddress2', orderInfoPreview.providerAddress2);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerCity'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerCity"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerCity') && props.formButtonClicked
                ? 'Please enter provider city.'
                : null
            "
            label="providerCity">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerCity"
                type="text"
                placeholder="City"
                label="City*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerCity', orderInfoPreview.providerCity);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerState'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerState"
            :error-messages="
              props.isEditable && !fieldNotEmpty('providerState') && props.formButtonClicked
                ? 'Please enter provider state.'
                : null
            "
            label="providerState">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-select
                v-model="orderInfoPreview.providerState"
                :items="states"
                placeholder="Select State"
                label="Provider State*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerState', orderInfoPreview.providerState);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="4">
          <FeedbackInputWrapper
            :field-id="'providerZip'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.providerZip"
            :error-messages="
              props.isEditable && !zipCodeValid('providerZip') && props.formButtonClicked
                ? 'Please enter provider ZIP.'
                : null
            "
            label="providerZip">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.providerZip"
                type="text"
                placeholder="ZIP"
                label="ZIP*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'providerZip', orderInfoPreview.providerZip);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12">
          <ProviderResponseForwarding
            ref="providerResponseForwardingRef"
            :enabled="forwardResponsesToProviderByEmail"
            :email="orderInfoPreview.providerEmail"
            :readonly="!props.isEditable"
            :disabled="!props.isEditable"
            :show-errors="props.formButtonClicked"
            @update:enabled="onForwardResponsesEnabledChange"
            @update:email="onProviderEmailChange" />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { usaStates } from "@/utils/usaStates";
import { isNPIValid, isPhoneNumberValid, isZipCodeValid } from "@/utils/validators";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";
import ProviderResponseForwarding from "@/components/ProviderResponseForwarding.vue";
import { mainServices } from "@/services/mainServices";
import debounce from "lodash.debounce";

const states = ref([]);
onMounted(async () => {
  for (let stateData of Object.values(usaStates)) {
    states.value.push(stateData.name);
  }
  // Check if client company has any providers in the database
  await checkIfProvidersExist();
});

const convertStateToFullName = (stateValue) => {
  if (!stateValue) return "";
  const trimmed = stateValue.trim();

  // Check if it's already a full state name
  if (states.value.includes(trimmed)) {
    return trimmed;
  }

  // Try to find by abbreviation
  const upperAbbrev = trimmed.toUpperCase();
  for (let stateData of Object.values(usaStates)) {
    if (stateData.abbreviation === upperAbbrev) {
      return stateData.name;
    }
  }

  // Return original if no match found
  return trimmed;
};

const emit = defineEmits(["updateOrderInfoField", "clearProviderSuccess"]);

const props = defineProps({
  orderInfo: {
    type: Object,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const orderInfoPreview = ref(props.orderInfo);

watch(
  () => props.orderInfo,
  (value) => {
    orderInfoPreview.value = value;
  }
);

const providerResponseForwardingRef = ref(null);

const forwardResponsesToProviderByEmail = computed(() =>
  Boolean(orderInfoPreview.value.forwardResponsesToProviderByEmail)
);

function onForwardResponsesEnabledChange(enabled) {
  emit("updateOrderInfoField", "forwardResponsesToProviderByEmail", enabled);
  if (!enabled) {
    onProviderEmailChange(null);
  }
}

function onProviderEmailChange(email) {
  emit("updateOrderInfoField", "providerEmail", email || null);
}

function validProviderResponseForwarding() {
  return providerResponseForwardingRef.value?.validate() ?? true;
}

defineExpose({ validProviderResponseForwarding });

// Provider search
const selectedProvider = ref(null);
const providerSearchTerm = ref("");
const providerSearchResults = ref([]);
const providerSearchLoading = ref(false);
const providerSearchNextPage = ref(null);
const providerSearchCurrentPage = ref(1);
const hasProviders = ref(false);

// Disable client-side filtering - backend handles search filtering
const providerCustomFilter = () => true;

// Check if the client company has any providers in the database
// Uses count endpoint to determine if provider search should be shown
const checkIfProvidersExist = async () => {
  try {
    const response = await mainServices.getProvidersCount();
    hasProviders.value = (response.count || 0) > 0;
  } catch (error) {
    // If check fails, default to hiding the search bar to be safe
    hasProviders.value = false;
  }
};

// Search providers with client company filtering
// Note: The backend automatically filters providers by the authenticated user's client company
// via client_company_filter, ensuring only applicable providers are returned
const searchProviders = async (query, page = 1) => {
  if (!query || query.trim().length === 0) {
    providerSearchResults.value = [];
    providerSearchNextPage.value = null;
    providerSearchCurrentPage.value = 1;
    return;
  }

  providerSearchLoading.value = true;
  try {
    const results = await mainServices.searchProviders(query.trim(), page);
    const newProviders = results.results.map((provider) => {
      const displayName = `${provider.firstName || ""} ${provider.lastName || ""}`.trim();
      return {
        title: displayName,
        value: {
          ...provider,
          displayName,
        },
        raw: {
          ...provider,
          displayName,
        },
      };
    });

    if (page === 1) {
      providerSearchResults.value = newProviders;
    } else {
      // Remove the "Load more" item if it exists
      providerSearchResults.value = providerSearchResults.value.filter((item) => !item.raw?.isLoadMore);
      providerSearchResults.value = [...providerSearchResults.value, ...newProviders];
    }

    // Add "Load more" option if there's a next page
    if (results.next) {
      providerSearchResults.value.push({
        title: "Load more results...",
        value: null,
        raw: { isLoadMore: true },
      });
    }

    providerSearchNextPage.value = results.next ? page + 1 : null;
    providerSearchCurrentPage.value = page;
  } catch (error) {
    if (page === 1) {
      providerSearchResults.value = [];
    }
    providerSearchNextPage.value = null;
  } finally {
    providerSearchLoading.value = false;
  }
};

const loadMoreProviders = () => {
  if (providerSearchNextPage.value && providerSearchTerm.value) {
    searchProviders(providerSearchTerm.value, providerSearchNextPage.value);
  }
};

const debouncedProviderSearch = debounce(searchProviders, 500);

const handleProviderSearch = (value) => {
  providerSearchTerm.value = value;
  if (value) {
    providerSearchCurrentPage.value = 1;
    debouncedProviderSearch(value);
  } else {
    providerSearchResults.value = [];
    providerSearchNextPage.value = null;
    providerSearchCurrentPage.value = 1;
  }
};

const handleProviderSelection = (item) => {
  if (item && item.value) {
    const provider = item.value;
    orderInfoPreview.value.providerFirstName = provider.firstName || "";
    orderInfoPreview.value.providerLastName = provider.lastName || "";
    orderInfoPreview.value.providerNpiNumber = provider.npi || "";
    orderInfoPreview.value.providerContactPhone = provider.contactPhone || "";
    orderInfoPreview.value.providerContactFax = provider.contactFax || "";
    orderInfoPreview.value.providerAddress1 = provider.address1 || "";
    orderInfoPreview.value.providerAddress2 = provider.address2 || "";
    orderInfoPreview.value.providerCity = provider.city || "";
    orderInfoPreview.value.providerState = convertStateToFullName(provider.state || "");
    orderInfoPreview.value.providerZip = provider.zip || "";

    emit("updateOrderInfoField", "providerFirstName", orderInfoPreview.value.providerFirstName);
    emit("updateOrderInfoField", "providerLastName", orderInfoPreview.value.providerLastName);
    emit("updateOrderInfoField", "providerNpiNumber", orderInfoPreview.value.providerNpiNumber);
    emit("updateOrderInfoField", "providerContactPhone", orderInfoPreview.value.providerContactPhone);
    emit("updateOrderInfoField", "providerContactFax", orderInfoPreview.value.providerContactFax);
    emit("updateOrderInfoField", "providerAddress1", orderInfoPreview.value.providerAddress1);
    emit("updateOrderInfoField", "providerAddress2", orderInfoPreview.value.providerAddress2);
    emit("updateOrderInfoField", "providerCity", orderInfoPreview.value.providerCity);
    emit("updateOrderInfoField", "providerState", orderInfoPreview.value.providerState);
    emit("updateOrderInfoField", "providerZip", orderInfoPreview.value.providerZip);
  }
};

// Validations
function fieldNotEmpty(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "";
}

function addressValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && value.length <= 35;
}

function npiValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isNPIValid(value);
}

function phoneValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isPhoneNumberValid(value);
}

function zipCodeValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isZipCodeValid(value);
}

// Watch for field changes to clear success message when user edits
// Clear success state when provider fields change to indicate user is making edits
watch(
  () => [
    orderInfoPreview.value.providerFirstName,
    orderInfoPreview.value.providerLastName,
    orderInfoPreview.value.providerNpiNumber,
  ],
  () => {
    emit("clearProviderSuccess");
  },
  { immediate: false }
);
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
